import { UpSelectedArrow } from 'assets/svg';
import Down from 'assets/svg/Down';
import { useOnClickOutside } from 'hooks/general/useOnOutsideClick';
import _ from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { trackEventMixpanel } from 'utils/mixpanel';

interface props {
    list: any;
    customOnChange?: Function;
    checkedListObject: any;
    disabled: Boolean;
    setSelectedList: Function;
    selectedList: any;
    customSelectAllFunction?: Function;
    selectAll?: boolean;
    searchable?: boolean;
    style?: React.CSSProperties;
    innerDivStyle?: React.CSSProperties;
    className?: string;
    defaultDisplayLabel?: string;
    eventMixPanelString?: string;
}

function SelectionDropDown({
    list,
    customOnChange,
    checkedListObject,
    disabled,
    setSelectedList,
    selectedList,
    customSelectAllFunction,
    selectAll = true,
    searchable = false,
    style = {},
    innerDivStyle = {},
    defaultDisplayLabel = '',
    className = '',
    eventMixPanelString,
}: props) {
    const [toggleDropDown, setToggleDropDown] = useState(false);
    const [searchKey, setSearchKey] = useState('');
    const [selectedItems, setSelectedItems] = useState(selectedList);
    const isListEmpty = !Array.isArray(list) || list.length === 0;

    useEffect(() => {
        setSelectedItems(selectedList);
    }, [selectedList]);

    const outsideClickRef = useRef<any>(null);

    //close expanded filter on clicking outside
    const clickOutsideHandler = useCallback(
        (event: any) => {
            if (toggleDropDown) {
                setToggleDropDown(false);
                setSearchKey('');
                setSelectedItems(selectedList);
            }
        },
        [toggleDropDown],
    );
    useOnClickOutside(outsideClickRef, clickOutsideHandler);

    const selectAllItems = (e: any) => {
        setSelectedItems(() => {
            if (e.target.checked) {
                let allPromo = list?.map((promo: any) => {
                    return promo.value;
                });
                return allPromo;
            } else {
                let filteredList = [list[0].value];
                return filteredList;
            }
        });
        eventMixPanelString &&
            trackEventMixpanel(eventMixPanelString, {
                dropdownValue: 'Select All',
                isChecked: e.target.checked,
            });
    };
    const labelText =
        defaultDisplayLabel ||
        (selectedList.length == 1
            ? list?.filter((item: any) => item.value === selectedList[0])[0]?.label || ''
            : 'Multiple selections');

    const onClickApply = () => {
        setSelectedList(selectedItems);
        setToggleDropDown(false);
    };

    const handleDropdownChange = (e: any, item: any) => {
        if (disabled) return;
        setSelectedItems((oldData: any) => {
            let data = _.cloneDeep(oldData);
            if (e.target.checked) {
                data.push(item.value);
                return data;
            } else {
                if (data.length == 1) return data;
                let filteredList = data.filter((data: any) => data !== item.value);
                return filteredList;
            }
        });
        eventMixPanelString &&
            trackEventMixpanel(eventMixPanelString, {
                dropdownValue: item?.label,
                isChecked: e.target.checked,
            });
    };

    return (
        <div
            ref={outsideClickRef}
            className={`inline-block relative dropdown-height dropdown-width ${className} ${
                disabled ? '!bg-[#f5f5f5] !text-[#00000040] cursor-not-allowed' : 'bg-white cursor-pointer'
            }`}
            style={{ ...style }}
        >
            <div
                className="h-full px-3 flex items-center gap-1 rounded justify-between border border-purpleFont border-0 focus:outline-none overflow-hidden"
                style={{ ...innerDivStyle }}
                onClick={() => {
                    if (disabled) return;
                    setToggleDropDown(!toggleDropDown);
                }}
            >
                {searchable ? (
                    <input
                        style={{ width: '100%', outline: 'none' }}
                        type="text"
                        className="px-1 bg-white rounded text-sm border-0 focus:outline-none w-36 h-8 placeholder-black"
                        placeholder={selectedItems.length === list.length ? 'All' : 'Multiple selections'}
                        value={searchKey}
                        onChange={(e) => setSearchKey(e.target.value)}
                        onClick={(e) => {
                            setToggleDropDown(true);
                            e.stopPropagation();
                        }}
                    />
                ) : (
                    <div
                        style={{
                            maxWidth: 'calc(100% - 20px)',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                        className="font-xs placeholder-black"
                        title={labelText}
                    >
                        {labelText}
                    </div>
                )}

                <span className="z-10 font-normal text-center text-slate-300 rounded text-base items-center justify-center w-[1.5rem] mr-[-15px]">
                    {toggleDropDown ? <UpSelectedArrow /> : <Down />}
                </span>
            </div>

            <div
                className={`bg-white dropdown-menu absolute z-50 text-primary shadow-l drop-shadow-lg rounded-b w-full ${
                    toggleDropDown ? '' : 'hidden'
                }`}
            >
                {/* Scrollable List */}
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {isListEmpty ? (
                        <div className="px-3 py-4 text-center font-xs text-[#00000073]">No data available</div>
                    ) : (
                        <ul className="pt-1 m-0">
                            {selectAll && (
                                <li
                                    className={`px-3 py-1 ${
                                        list?.length === selectedItems?.length ? 'bg-bgBackground' : ''
                                    }`}
                                >
                                    <label className="w-full font-xs flex items-center gap-4 cursor-pointer">
                                        <input
                                            style={{ opacity: disabled ? '0.2' : 1 }}
                                            type="checkbox"
                                            className="checkbox-size accent-purpleFont"
                                            checked={list?.length === selectedItems?.length}
                                            onClick={(e) => {
                                                if (disabled) return;
                                                customSelectAllFunction
                                                    ? customSelectAllFunction(setSelectedItems, e)
                                                    : selectAllItems(e);
                                            }}
                                        />
                                        <span>Select All</span>
                                    </label>
                                </li>
                            )}
                            {list
                                ?.filter((item: any) => item.label.toLowerCase().includes(searchKey.toLowerCase()))
                                .map((item: any, index: number) => (
                                    <li
                                        key={index}
                                        className={`px-3 py-1 ${
                                            selectedItems.includes(item.value) ? 'bg-bgBackground' : ''
                                        }`}
                                    >
                                        <label
                                            className={`text-base flex gap-2 items-center ${
                                                disabled ? '' : 'cursor-pointer'
                                            }`}
                                            key={index + item.label}
                                        >
                                            <input
                                                style={{ opacity: disabled ? '0.2' : 1 }}
                                                type="checkbox"
                                                className={`checkbox-size min-w-[1rem] accent-purpleFont ${
                                                    disabled ? '' : 'cursor-pointer'
                                                }`}
                                                checked={selectedItems.includes(item.value) || false}
                                                onChange={(e) => {
                                                    customOnChange
                                                        ? customOnChange(setSelectedItems, e, item, selectedItems)
                                                        : handleDropdownChange(e, item);
                                                }}
                                            />
                                            <span style={{ wordBreak: 'break-word' }} className="font-xs ml-2">
                                                {item.label}
                                            </span>
                                        </label>
                                    </li>
                                ))}
                        </ul>
                    )}
                </div>

                {/* Fixed Buttons */}
                <div className="p-2  border-t border-borderGreyAl1 flex justify-end sticky bottom-0 space-x-2">
                    <button
                        className="px-2 py-1 text-purpleFont text-[13px] rounded border border-purpleFont"
                        onClick={() => {
                            if (isListEmpty) return;
                            setSelectedItems(selectedList);
                            setSearchKey('');
                        }}
                    >
                        Reset
                    </button>
                    <button
                        className="px-2 py-1 bg-purpleFont rounded text-white text-[13px] border-purpleFont"
                        onClick={() => {
                            if (isListEmpty) return;
                            onClickApply();
                        }}
                    >
                        Apply
                    </button>
                </div>
            </div>
        </div>
    );
}

export default SelectionDropDown;
